<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import { useElementSize, useSwipe } from '@vueuse/core'

import BaseEmoji from '@dogfy/components/base/BaseEmoji.vue'
import BaseBubble from '@dogfy/components/base/BaseBubble.vue'
import BaseIcon from '@dogfy/components/base/BaseIcon.vue'
import BaseButton from '@dogfy/components/base/BaseButton.vue'
import AppButtonX from '@dogfy/components/layout/AppButtonX.vue'

import { roundToDecimals } from '@dogfy/lib/utils'

const props = defineProps({
  type: {
    type: String,
    default: 'info',
    validator: (value) => ['info', 'success', 'warning', 'error'].includes(value),
  },
  actions: {
    type: Object,
    default: null,
  },
  isClosable: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['close'])

const toastTypes = {
  info: {
    emoji: '🐶',
    icon: 'information-circle-solid',
  },
  success: {
    emoji: '🥳',
    icon: 'check-circle-solid',
  },
  warning: {
    emoji: '🧐',
    icon: 'exclamation-circle-solid',
  },
  error: {
    emoji: '🫣',
    icon: 'x-circle-solid',
  },
}

const toastType = computed(() => toastTypes[props.type])

const toastRef = ref(null)
const { isSwiping, lengthX: swipingLengthX } = useSwipe(toastRef, { threshold: 1 })
const swipingRatio = computed(() => Math.abs(swipingLengthX.value) / toastRef.value?.offsetWidth)

const toastContentRef = ref(null)
const { height: contentHeight } = useElementSize(toastContentRef)
const contentLineHeight = ref(0)

const isMultiline = computed(() => contentHeight.value && contentLineHeight.value ? roundToDecimals(contentHeight.value, 1) >= roundToDecimals(2 * contentLineHeight.value, 1) : true)

function manageAction (type) {
  props.actions[type].action()

  if (props.isClosable) {
    emit('close')
  }
}

watch(swipingRatio, (swipingRatio) => {
  if (swipingRatio > 0.25 && props.isClosable) {
    emit('close')
  }
})

onMounted(() => {
  contentLineHeight.value = Number(window.getComputedStyle(toastContentRef.value).lineHeight?.replace('px', '') || 0)

  // Auto-remove the toast based on its type.
  if (['info', 'success', 'warning'].includes(props.type) && props.isClosable) {
    setTimeout(() => {
      emit('close')
    }, 8000)
  }
})
</script>

<template>
  <div
    ref="toastRef"
    class="toast-notification"
    :class="{
      'toast-notification--multiline': isMultiline,
      'toast-notification--swiping': isSwiping && swipingRatio <= 0.25 && props.isClosable,
      'toast-notification--hidden': swipingRatio > 0.25 && props.isClosable,
    }"
    role="alert"
  >
    <BaseBubble class="toast-notification__bubble" theme="info">
      <BaseEmoji :emoji="toastType.emoji" />
      <BaseIcon class="toast-notification__bubble-icon" :class="`toast-notification__bubble-icon--${props.type}`" :icon="toastType.icon" />
    </BaseBubble>
    <div ref="toastContentRef" class="toast-notification__content">
      <p class="toast-notification__message">
        <slot />
      </p>
      <div v-if="props.actions" class="toast-notification__buttonset">
        <BaseButton
          v-if="props.actions.secondary"
          type="link-neutral-white small"
          @click="manageAction('secondary')"
        >
          {{ props.actions.secondary.text }}
        </BaseButton>
        <BaseButton
          v-if="props.actions.primary"
          type="outlined extra-small"
          @click="manageAction('primary')"
        >
          {{ props.actions.primary.text }}
        </BaseButton>
      </div>
    </div>
    <AppButtonX
      v-if="props.isClosable"
      class="toast-notification__x-button"
      theme="inverted"
      @click="emit('close')"
    />
  </div>
</template>

<style lang="scss">
.toast-notification {
  display: flex;
  align-items: center;
  gap: $spacer;
  padding: $spacer-three-quarters;
  background-color: $color-secondary-dark;
  box-shadow: $drop-shadow-lg;
  box-sizing: border-box;
  border-radius: $border-radius-pill;
  pointer-events: auto;
  width: fit-content;
  user-select: none;

  &--multiline {
    border-radius: $border-radius-lg;
    align-items: flex-start;
  }

  &--hidden {
    transition: all $transition-duration-slowest ease;
    transform: translateX(calc(v-bind(swipingLengthX) * -1px));
    opacity: 0;
  }

  &--swiping {
    transform: translateX(calc(v-bind(swipingLengthX) * -1px));
    opacity: max(1 - v-bind(swipingRatio), 0.5);
  }

  &__content {
    display: flex;
    flex-wrap: wrap;
    column-gap: $spacer;
    row-gap: $spacer-half;
    font-size: ms(0, true);
    line-height: $font-lineheight-large;
    font-weight: $font-weight-regular;
    color: $color-neutral-white;
    align-items: center;
  }

  &__message {
    @include breakpoint(lg) {
      margin-right: $spacer-half;
    }
  }

  &__bubble {
    position: relative;
    width: 32px;
    height: 32px;
    font-size: ms(1, true);

    &--multiline {
      width: 48px;
      height: 48px;
      font-size: ms(3, true);
    }
  }

  &__bubble-icon {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 14px;
    height: 14px;

    &--info {
      color: $color-feedback-info-dark;
    }

    &--success {
      color: $color-feedback-success-dark;
    }

    &--warning {
      color: $color-feedback-warning-darkest;
    }

    &--error {
      color: $color-feedback-error-dark;
    }
  }

  &__x-button {
    flex-shrink: 0;
  }

  &__buttonset {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: $spacer-half;

    @include breakpoint(lg) {
      flex-direction: row;
      justify-content: center;
      gap: $spacer;
    }
  }
}
</style>
